import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { ViewMode } from '@pages/checkout-app/checkout-layout/main/components/extensions/models';
import { ShopService } from '@services/shops/shop.service';
import { MatDialog } from '@angular/material/dialog';
import { TextDialogComponent } from '@app/sharedModule/components/text-dialog/text-dialog.component';
import { CustomUpsellExtension } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { CheckoutExampleService } from '../checkout-example.service';
import {
  TIMER_STATES,
  EXTENSION_TIMER_CONFIGS,
  TIME_FORMATS,
  APPEARANCE_TYPES,
} from '@pdqdev/pdq-shared/dist/utils/extensions-timer-utils';
import {
  getTimerPreviewMode,
  getExampleCountdown,
  isMessageEmpty,
  getTimerMessageText,
  getTimerBarAlignment,
  getTimerStyleClass,
  getTimerBarBannerIcon,
} from '@utils/timer-utils';

@Component({
  selector: 'vip-process-example',
  templateUrl: './vip-process-example.component.html',
  styleUrls: ['./vip-process-example.component.scss'],
  standalone: false,
})
export class VipProcessExampleComponent implements OnChanges, OnDestroy {
  @Input() template;
  @Input() mode: ViewMode = null;
  @Input() isMobile: boolean = false;
  currency = '$';
  title: string = 'VIP processing';
  description: string = 'Get special service for your order';
  upsellRate: string = '6';
  discount: string = '20';

  orderSubtotal = 20;

  productImageBase64: string | null = null;
  markAsChecked: boolean = false;
  hideCheckbox: boolean = false;
  applyDiscountToSubTotal: boolean = false;
  descriptionDiscountPlaceholder = '{upsell_discount}';
  trustBadgeStyle: Record<string, string> = {};
  private readonly trustBadgeBackgroundCssMap: Record<string, string> = {
    transparent: 'transparent',
    base: 'var(--surface-bright)',
    subdued: 'var(--surface-container-low)',
  };
  private readonly trustBadgeRadiusCssMap: Record<string, string> = {
    none: '0',
    small: 'var(--border-radius-s)',
    base: 'var(--border-radius-m)',
    large: 'var(--border-radius-l)',
  };
  private readonly trustBadgeBorderStyleMap: Record<string, string> = {
    base: 'solid',
    dotted: 'dotted',
  };
  constructor(
    private shopService: ShopService,
    private matDialog: MatDialog,
    private checkoutExampleService: CheckoutExampleService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    this.currency = this.shopService.shopCurrencySymbol;
    this.setFixedComponentData(this.template);
  }

  handleTooltipClick() {
    this.matDialog.open(TextDialogComponent, {
      data: {
        headerText: this.template.tooltipTitle,
        bodyTexts: [this.template.tooltipText],
      },
    });
  }

  ngOnDestroy(): void {
    this.checkoutExampleService.showOrderSummaryPreview.set(false);
  }

  setFixedComponentData(template) {
    if (template?.title) {
      this.title = template?.title;
    }

    if (template?.discount) {
      this.discount = template?.discount;
    }
    if (template?.upsellRate) {
      this.upsellRate = template?.upsellRate;
    }
    const autoAdd = template?.autoAdd;
    this.markAsChecked = autoAdd ? !!autoAdd.enabled : template?.markAsChecked;
    this.hideCheckbox = !!autoAdd && autoAdd.mode === 'persistent' && autoAdd.persistentCheckbox === 'hidden';
    this.productImageBase64 = template.productImageBase64;
    this.applyDiscountToSubTotal =
      template?.applyDiscountTo === CustomUpsellExtension.ApplyDiscountTo.ORDER_SUBTOTAL &&
      template.upsellRateType === CustomUpsellExtension.UpsellRateType.FIXED;

    if (this.applyDiscountToSubTotal) {
      this.discount =
        (this.checkoutExampleService.orderSubtotal() - +this.upsellRate) * (1 - +template.discount / 100) +
        +this.upsellRate +
        '';
      this.checkoutExampleService.showOrderSummaryPreview.set(true);
    } else if (template?.upsellRate && Boolean(template?.discount)) {
      this.checkoutExampleService.showOrderSummaryPreview.set(false);
      const rate = +template.upsellRate;

      const discount = +template.discount;
      const calculatedDiscount = (rate * discount) / 100;
      if (+template.discount > 0) {
        this.discount = rate + '';
      }
      this.upsellRate = rate - calculatedDiscount + '';
    }

    if (template?.description) {
      this.description = template?.description.replace(this.descriptionDiscountPlaceholder, template.discount + '%');
    }

    this.computeTrustBadgeStyle(template?.trustSignals);
  }

  private computeTrustBadgeStyle(trustSignals?: CustomUpsellExtension.TrustSignals): void {
    const background = trustSignals?.backgroundColor ?? 'transparent';
    const border = trustSignals?.border ?? 'none';
    const cornerRadius = trustSignals?.cornerRadius ?? 'base';
    const hasBorder = border !== 'none';
    const hasContainer = background !== 'transparent' || hasBorder;
    if (!hasContainer) {
      this.trustBadgeStyle = {};
      return;
    }
    this.trustBadgeStyle = {
      background: this.trustBadgeBackgroundCssMap[background] ?? 'transparent',
      'border-radius': this.trustBadgeRadiusCssMap[cornerRadius] ?? this.trustBadgeRadiusCssMap['base'],
      'border-style': hasBorder ? (this.trustBadgeBorderStyleMap[border] ?? 'solid') : 'none',
      'border-width': hasBorder ? '1px' : '0',
      'border-color': hasBorder ? 'var(--surface-container-high)' : 'transparent',
      padding: 'var(--padding-4) var(--padding-8)',
    };
  }

  private getTimerPreviewMode(): string {
    return getTimerPreviewMode(this.template?.timerConfiguration, TIMER_STATES.DEFAULT);
  }

  private getExampleCountdown(): string {
    return getExampleCountdown(this.template?.timerConfiguration, TIME_FORMATS.DIGITAL);
  }

  isTimerMessageEmpty(): boolean {
    const previewMode = this.getTimerPreviewMode();
    const timerConfig = this.template?.timerConfiguration;

    if (previewMode === TIMER_STATES.EXPIRED) {
      return isMessageEmpty(timerConfig?.expiredMessage);
    } else if (previewMode === TIMER_STATES.LOW) {
      return isMessageEmpty(timerConfig?.lowTimerMessage);
    } else {
      return isMessageEmpty(timerConfig?.defaultMessage);
    }
  }

  getTimerMessageText(): string {
    const previewMode = this.getTimerPreviewMode();
    const countdown = this.getExampleCountdown();
    return getTimerMessageText(this.template?.timerConfiguration, previewMode, countdown, TIMER_STATES);
  }

  getTimerMessageClass(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerStyleClass(
      this.template?.timerConfiguration,
      previewMode,
      'timer-message',
      TIMER_STATES,
      APPEARANCE_TYPES,
    );
  }

  getTimerBarText(): string {
    return this.getTimerMessageText();
  }

  getTimerBarAlignment(): string {
    const previewMode = this.getTimerPreviewMode();
    const timerConfig = this.template?.timerConfiguration;

    let message = timerConfig?.defaultMessage || '';
    if (previewMode === TIMER_STATES.EXPIRED) {
      message = timerConfig?.expiredMessage || '';
    }

    if (previewMode === TIMER_STATES.LOW) {
      message = timerConfig?.lowTimerMessage || '';
    }

    return getTimerBarAlignment(message);
  }

  getTimerBarClass(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerStyleClass(
      this.template?.timerConfiguration,
      previewMode,
      'timer-bar',
      TIMER_STATES,
      APPEARANCE_TYPES,
    );
  }

  getTimerBadgeText(): string {
    return this.getTimerMessageText();
  }

  getTimerBadgeClass(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerStyleClass(
      this.template?.timerConfiguration,
      previewMode,
      'timer-badge',
      TIMER_STATES,
      APPEARANCE_TYPES,
    );
  }

  getTimerBarBannerIcon(): string {
    const previewMode = this.getTimerPreviewMode();
    return getTimerBarBannerIcon(this.template?.timerConfiguration, previewMode, TIMER_STATES, APPEARANCE_TYPES);
  }

  // Expose constants to template
  readonly TIMER_PLACEMENTS = EXTENSION_TIMER_CONFIGS.DIGITAL_UPSELL.TIMER_PLACEMENTS;
}
